import { useTranslation } from '@pancakeswap/localization'
import { Flex, Heading, NextLinkFromReactRouter, Text } from '@pancakeswap/uikit'
import Container from 'components/Layout/Container'
import Page from 'components/Layout/Page'
import styled from 'styled-components'
import { DEX_LABEL, DexId, SMART_SWAP_PATH, TAB_DEX_IDS, VersionParam, useInfoRoute, useInfoSummary } from 'state/infoHub/hooks'

/**
 * One compact header strip, PancakeSwap-Info style: the title and the scope control on the first line,
 * the view tabs and the version chips on the second (only inside a DEX). No row labels: the control
 * type says what it is — a filled segment is a scope, an underlined tab is a view, a small chip is a version.
 */
const Strip = styled.div`
  background: ${({ theme }) => theme.colors.gradientCardHeader};
  border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
`
const TopRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  padding: 14px 0 10px;
  ${({ theme }) => theme.mediaQueries.sm} {
    padding: 18px 0 12px;
  }
`
const SecondRow = styled(Flex)`
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 16px;
`
const Segmented = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.input};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`
const Segment = styled(NextLinkFromReactRouter)<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  color: ${({ theme, $on }) => ($on ? theme.colors.invertedContrast : theme.colors.textSubtle)};
  background: ${({ theme, $on }) => ($on ? theme.colors.primary : 'transparent')};
  transition: background 120ms ease-out, color 120ms ease-out;
  &:hover {
    color: ${({ theme, $on }) => ($on ? theme.colors.invertedContrast : theme.colors.text)};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`
const ViewTabs = styled.div`
  display: inline-flex;
  gap: 4px;
  margin-bottom: -1px;
`
const ViewTab = styled(NextLinkFromReactRouter)<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 12px;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme, $on }) => ($on ? theme.colors.text : theme.colors.textSubtle)};
  border-bottom: 2px solid ${({ theme, $on }) => ($on ? theme.colors.primary : 'transparent')};
  transition: color 120ms ease-out, border-color 120ms ease-out;
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`
const Chips = styled(Flex)`
  align-items: center;
  gap: 4px;
  padding-bottom: 6px;
`
const Chip = styled(NextLinkFromReactRouter)<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme, $on }) => ($on ? theme.colors.text : theme.colors.textSubtle)};
  border: 1px solid ${({ theme, $on }) => ($on ? theme.colors.primary : theme.colors.cardBorder)};
  background: ${({ theme, $on }) => ($on ? theme.colors.backgroundAlt : 'transparent')};
  transition: border-color 120ms ease-out, color 120ms ease-out;
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`

const VERSIONS: VersionParam[] = ['all', 'v2', 'v3']

export const InfoHubLayout: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation()
  const { dex, version, section, address, hrefFor, isSmartSwap } = useInfoRoute()
  const { summary } = useInfoSummary(dex, version)
  const supported = summary?.supportedVersions ?? (dex ? ['v2', 'v3'] : [])
  const versionOptions = VERSIONS.filter((v) => v === 'all' || (supported as string[]).includes(v))
  return (
    <>
      <Strip>
        <Container>
          <TopRow>
            {/* a pool or token page has its own h1 (the pair or the token); the strip title is then plain text */}
            <Heading scale="lg" as={address ? 'div' : 'h1'}>
              {dex === 'pancakeswap' ? t('PancakeSwap pools on CADINU Markets') : isSmartSwap ? t('CADINU Markets') : t('CADINUdex Markets')}
            </Heading>
            {/* Total · CADINUdex · Smart Swap (2026-10-06: Smart Swap replaces the PancakeSwap tab) */}
            <Segmented role="navigation" aria-label={t('Scope')}>
              <Segment to="/info" $on={!dex && !isSmartSwap} data-testid="tab-total" aria-current={!dex && !isSmartSwap ? 'page' : undefined}>
                {t('Total')}
              </Segment>
              {TAB_DEX_IDS.map((id) => (
                <Segment key={id} to={hrefFor(id, 'all', 'overview')} $on={dex === id} data-testid={`tab-${id}`} aria-current={dex === id ? 'page' : undefined}>
                  {DEX_LABEL[id]}
                </Segment>
              ))}
              <Segment to={SMART_SWAP_PATH} $on={isSmartSwap} data-testid="tab-smart-swap" aria-current={isSmartSwap ? 'page' : undefined}>
                {t('Smart Swap')}
              </Segment>
            </Segmented>
          </TopRow>
          {/* a PancakeSwap pool or token page has no overview or lists of its own any more (they land on Smart Swap): no second row */}
          {dex && dex !== 'pancakeswap' && (
            <SecondRow>
              <ViewTabs role="navigation" aria-label={t('View')}>
                <ViewTab to={hrefFor(dex as DexId, version, 'overview')} $on={section === 'overview'} data-testid="tab-overview">
                  {t('Overview')}
                </ViewTab>
                <ViewTab to={hrefFor(dex as DexId, version, 'pools')} $on={section === 'pools'} data-testid="tab-pools">
                  {t('Pools')}
                </ViewTab>
                <ViewTab to={hrefFor(dex as DexId, version, 'tokens')} $on={section === 'tokens'} data-testid="tab-tokens">
                  {t('Tokens')}
                </ViewTab>
              </ViewTabs>
              <Chips role="navigation" aria-label={t('Version')}>
                <Text fontSize="12px" color="textSubtle" mr="4px">
                  {t('Version')}
                </Text>
                {versionOptions.map((v) => (
                  <Chip key={v} to={hrefFor(dex as DexId, v)} $on={version === v} data-testid={`tab-version-${v}`}>
                    {v === 'all' ? t('All') : v.toUpperCase()}
                  </Chip>
                ))}
              </Chips>
            </SecondRow>
          )}
        </Container>
      </Strip>
      <Page>{children}</Page>
    </>
  )
}

export default InfoHubLayout
